import React, { useEffect, useState } from 'react';
import { Button, Card, Form, List, Space, Switch, Tag, Tooltip, Typography, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import useAuth from '../../hooks/useAuth';
import { useBot, useBotApps, useCalendars } from '../../hooks/useWpbot';
import wpbotService from '../../services/wpbotService';
import { TBotApp, TCalendar, TCustomEvent } from '../../models/wpbotModel';
import { BOT_APP_CATALOG, BOT_APP_LABELS, CALENDAR_APP_KEY } from '../../config/botApps';
import BotCalendarFields from './BotCalendarFields';
import {
  calendarFirestoreUid,
  calendarServices,
  calendarSqlId,
  findCalendar,
  sanitizeCustomEvents,
} from '../../utils/calendars';

/**
 * Panel de apps del bot. Calendario se configura acá (elegir agenda +
 * servicios) y solo se puede activar cuando ya hay un calendario guardado.
 */
const BotAppsPanel: React.FC<{ botId: string }> = ({ botId }) => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const canUpdate = user.permissions.actions.join().includes('wpbot.action.bot.update')
  const companyUid = company.current?.uid

  const { data: appsData, error, mutate: mutateApps } = useBotApps(tokenData, { bot_id: botId, company: companyUid });
  const { data: botData, mutate: mutateBot } = useBot(tokenData, { id: botId, company: companyUid });
  const { data: calendarsData, isLoading: loadingCalendars, mutate: mutateCalendars } = useCalendars(tokenData, { company: companyUid });

  const apps: TBotApp[] = appsData?.status === 200 ? (appsData?.apps || []) : []
  const calendars: TCalendar[] = calendarsData?.status === 200 ? (calendarsData?.calendarList || []) : []
  const calendarIdWatch: string | undefined = Form.useWatch('calendar_id', form)
  const savedCalendarId = botData?.status === 200 ? String(botData.calendar_id || '') : ''
  const calendarApp = apps.find((app) => app.app_key === CALENDAR_APP_KEY)
  const calendarEnabled = Boolean(calendarApp?.is_enabled)
  const canEnableCalendar = Boolean(savedCalendarId)

  useEffect(() => {
    if (error) {
      message.error("Error al cargar las aplicaciones del bot");
    }
  }, [error]);

  useEffect(() => {
    if (botData?.status !== 200) return
    const cal = findCalendar(calendars, botData.calendar_id)
    form.setFieldsValue({
      calendar_id: cal ? (calendarFirestoreUid(cal) || calendarSqlId(cal)) : (savedCalendarId || undefined),
      customEvents: calendarServices(cal),
    })
  }, [botData, calendars, form, savedCalendarId])

  const onCalendarChange = (value: string | undefined) => {
    const cal = findCalendar(calendars, value)
    form.setFieldsValue({ customEvents: calendarServices(cal) })
  }

  const persistCalendarConfig = async (calendarId: string, customEvents: TCustomEvent[]) => {
    const cal = findCalendar(calendars, calendarId)
    const botRes = await wpbotService.updateBot(tokenData, {
      id: botId,
      calendar_id: calendarId,
      company: companyUid,
    })
    if (botRes?.error) return botRes
    const events = sanitizeCustomEvents(customEvents)
    const uidCalendar = cal ? calendarFirestoreUid(cal) : calendarId
    return wpbotService.updateCalendarCustomEvents(tokenData, {
      company: companyUid,
      uidCalendar,
      calendar_id: uidCalendar,
      customEvents: events,
    })
  }

  const handleSaveConfig = async () => {
    try {
      const values = await form.validateFields()
      if (!values.calendar_id) {
        message.warning("Selecciona un calendario antes de guardar")
        return
      }
      setSaving(true)
      const res = await persistCalendarConfig(values.calendar_id, values.customEvents || [])
      setSaving(false)
      if (res?.error) {
        message.error(res.message)
        return
      }
      message.success("Configuración del calendario guardada. Ya puedes activar la aplicación.")
      mutateBot()
      mutateCalendars()
    } catch {
      // validación del form
    }
  }

  const handleToggle = async (appKey: string, isEnabled: boolean) => {
    if (appKey === CALENDAR_APP_KEY && isEnabled && !canEnableCalendar) {
      message.warning("Guarda un calendario antes de activar el agendamiento")
      return
    }
    setToggling(true)
    if (appKey === CALENDAR_APP_KEY && isEnabled) {
      const selected = form.getFieldValue('calendar_id')
      if (selected && selected !== savedCalendarId) {
        const res = await persistCalendarConfig(selected, form.getFieldValue('customEvents') || [])
        if (res?.error) {
          setToggling(false)
          message.error(res.message)
          return
        }
      }
    }
    const res = await wpbotService.setAppEnabled(tokenData, {
      bot_id: botId,
      app_key: appKey,
      is_enabled: isEnabled,
      company: companyUid,
    })
    setToggling(false)
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success(`Aplicación ${BOT_APP_LABELS[appKey] || appKey} ${isEnabled ? 'habilitada' : 'deshabilitada'}`)
    mutateApps()
    mutateBot()
  }

  const catalogItems = BOT_APP_CATALOG.map((def) => {
    const row = apps.find((app) => app.app_key === def.key)
    return { def, row }
  })

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {catalogItems.map(({ def, row }) => {
        if (def.key === CALENDAR_APP_KEY) {
          return (
            <Card
              key={def.key}
              title={
                <Space>
                  {def.label}
                  <Tag color={calendarEnabled ? 'green' : 'default'}>
                    {calendarEnabled ? 'Habilitada' : 'Deshabilitada'}
                  </Tag>
                </Space>
              }
              extra={
                <Tooltip
                  title={canEnableCalendar ? undefined : 'Elige un calendario y guarda la configuración para poder activar esta app'}
                >
                  <span>
                    <Switch
                      checked={calendarEnabled}
                      loading={toggling}
                      disabled={!canUpdate || toggling || (!calendarEnabled && !canEnableCalendar)}
                      onChange={(checked) => handleToggle(CALENDAR_APP_KEY, checked)}
                    />
                  </span>
                </Tooltip>
              }
            >
              <Typography.Paragraph type="secondary">
                Elige el calendario y los servicios que el bot va a ofrecer (corte, barba, combinaciones, etc.).
                La app no se activa hasta que guardes esa configuración.
              </Typography.Paragraph>
              <Form
                form={form}
                layout="vertical"
                disabled={!canUpdate}
                initialValues={{ customEvents: [] }}
              >
                <BotCalendarFields
                  calendars={calendars}
                  loading={loadingCalendars}
                  calendarId={calendarIdWatch}
                  required
                  onCalendarChange={onCalendarChange}
                />

                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveConfig} loading={saving} disabled={!canUpdate}>
                  Guardar configuración
                </Button>
              </Form>
            </Card>
          )
        }

        const enabled = Boolean(row?.is_enabled)
        return (
          <List
            key={def.key}
            dataSource={[def]}
            renderItem={() => (
              <List.Item
                actions={[
                  <Switch
                    key="toggle"
                    checked={enabled}
                    disabled={!canUpdate || toggling}
                    onChange={(checked) => handleToggle(def.key, checked)}
                  />
                ]}
              >
                <List.Item.Meta
                  title={
                    <>
                      {def.label}{' '}
                      <Tag color={enabled ? 'green' : 'default'}>
                        {enabled ? 'Habilitada' : 'Deshabilitada'}
                      </Tag>
                    </>
                  }
                  description={def.description}
                />
              </List.Item>
            )}
          />
        )
      })}
    </Space>
  );
};

export default BotAppsPanel;
